'use client';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Olvasói auth — Zustand-Persist tárolja az access-tokent és a user-profilt.
 * A refresh-token mostantól httpOnly + Secure + SameSite=Lax cookie-ban
 * (`lectura_rt`, Path=/v1/auth) — a JS NEM látja és NEM tárolja, így XSS
 * esetén sem lopható. A `credentials: 'include'` minden auth-fetch-en
 * bekapcsolva, hogy a böngésző automatikusan elküldje a cookie-t.
 *
 * **localStorage-kulcs:** `lectura-reader-auth` (különbözik az admin-web
 * `lectura-admin-auth`-tól, hogy egy gépen mindkettő egymás mellett éljen).
 */
export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  roles: string[];
  tenant_id: string;
}

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  expiresAt: number | null;

  isAuthenticated: () => boolean;
  /** Login. Ha a user-nek aktív TOTP-secretje van, MFA-flow indul (`mfa_required`). */
  login: (email: string, password: string) => Promise<LoginResult>;
  /** MFA-flow 2. lépése — challenge-token + TOTP. `rememberDevice=true` esetén
   *  a backend httpOnly eszköz-cookie-t állít be (nem-admin szerepkörre), így
   *  ezen a böngészőn 10 napig nincs MFA. */
  verifyMfa: (challengeToken: string, totpCode: string, rememberDevice?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<boolean>;
  fetchMe: () => Promise<void>;
}

export type LoginResult =
  | { status: 'ok' }
  | { status: 'mfa_required'; mfaChallengeToken: string; expiresIn: number; trustedDeviceAllowed: boolean };

const TENANT = 'default';
const AUTH_FETCH_OPTS: Partial<RequestInit> = { credentials: 'include' };

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      user: null,
      expiresAt: null,

      isAuthenticated: () => {
        const s = get();
        if (!s.accessToken || !s.expiresAt) return false;
        // 30 mp puffer — ne küldjünk olyan tokent, ami másodperceken belül lejár.
        return s.expiresAt > Date.now() + 30_000;
      },

      login: async (email, password) => {
        const res = await fetch('/api/identity/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Tenant-Id': TENANT },
          body: JSON.stringify({ email, password }),
          ...AUTH_FETCH_OPTS,
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Belépés sikertelen (${res.status}): ${text.slice(0, 200)}`);
        }
        const data = await res.json();
        if (data.requires_mfa) {
          return {
            status: 'mfa_required',
            mfaChallengeToken: data.mfa_challenge_token,
            expiresIn: data.expires_in,
            trustedDeviceAllowed: Boolean(data.trusted_device_allowed),
          };
        }
        set({
          accessToken: data.access_token,
          expiresAt: Date.now() + data.expires_in * 1000,
        });
        await get().fetchMe();
        return { status: 'ok' };
      },

      verifyMfa: async (challengeToken, totpCode, rememberDevice = false) => {
        const res = await fetch('/api/identity/auth/mfa/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mfa_challenge_token: challengeToken,
            totp_code: totpCode,
            remember_device: rememberDevice,
          }),
          ...AUTH_FETCH_OPTS,
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`MFA ellenőrzés sikertelen (${res.status}): ${text.slice(0, 200)}`);
        }
        const data = await res.json();
        set({
          accessToken: data.access_token,
          expiresAt: Date.now() + data.expires_in * 1000,
        });
        await get().fetchMe();
      },

      logout: async () => {
        await fetch('/api/identity/auth/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          ...AUTH_FETCH_OPTS,
        }).catch(() => {});
        set({ accessToken: null, user: null, expiresAt: null });
      },

      refresh: async () => {
        // A refresh cookie a credentials: 'include' miatt automatikusan megy.
        const res = await fetch('/api/identity/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          ...AUTH_FETCH_OPTS,
        });
        if (!res.ok) {
          set({ accessToken: null, user: null, expiresAt: null });
          return false;
        }
        const data = await res.json();
        set({
          accessToken: data.access_token,
          expiresAt: Date.now() + data.expires_in * 1000,
        });
        return true;
      },

      fetchMe: async () => {
        const { accessToken } = get();
        if (!accessToken) return;
        const res = await fetch('/api/identity/users/me', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (res.ok) {
          const u = await res.json();
          set({
            user: {
              id: u.id,
              email: u.email,
              display_name: u.display_name,
              roles: u.roles,
              tenant_id: u.tenant_id,
            },
          });
        }
      },
    }),
    {
      name: 'lectura-reader-auth',
      version: 2,
      partialize: (s) => ({
        accessToken: s.accessToken,
        user: s.user,
        expiresAt: s.expiresAt,
      }),
      migrate: (persisted, version) => {
        // v1 → v2: a refreshToken localStorage-mezőt eldobjuk (most cookie-ban van)
        if (version < 2 && persisted && typeof persisted === 'object') {
          const rest = { ...(persisted as Record<string, unknown>) };
          delete rest.refreshToken;
          return rest as unknown as AuthState;
        }
        return persisted as AuthState;
      },
    },
  ),
);

/**
 * Authentikált fetch — minden requestbe beleteszi a Bearer-fejlécet, és ha
 * 401-et kap, egyszer megpróbál refresh-elni a refresh-cookie alapján.
 *
 * A `X-Tenant-Id` fejléc default `default` — egy multi-tenant deploymentnél
 * a tenant-választó UI-ból állítódik át.
 */
export async function authFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const auth = useAuth.getState();
  const headers = new Headers(init.headers ?? {});

  if (!headers.has('X-Tenant-Id')) headers.set('X-Tenant-Id', TENANT);

  let token = auth.accessToken;
  if (token && !auth.isAuthenticated()) {
    const ok = await auth.refresh();
    token = ok ? useAuth.getState().accessToken : null;
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let res = await fetch(input, { ...init, headers });

  if (res.status === 401) {
    const ok = await auth.refresh();
    if (ok) {
      headers.set('Authorization', `Bearer ${useAuth.getState().accessToken}`);
      res = await fetch(input, { ...init, headers });
    }
  }
  return res;
}
